import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { LogIn, LogOut, Package, Shield, User as UserIcon } from 'lucide-react';

export default function AccountMenu({ mobile = false, onNavigate }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    localStorage.setItem('zs_post_login', location.pathname === '/admin' ? '/' : location.pathname);
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => { await logout(); setOpen(false); onNavigate?.(); navigate('/'); };

  if (!user) {
    return (
      <Button data-testid="customer-login-btn" onClick={login} variant="outline"
        className={`${mobile ? 'w-full' : ''} rounded-full border-orange-500/60 text-orange-400 hover:bg-orange-500 hover:text-white font-bold uppercase tracking-wider bg-transparent h-10`}>
        <LogIn className="w-4 h-4 mr-2" /> Sign In
      </Button>
    );
  }

  const initial = (user.name || user.email || '?').charAt(0).toUpperCase();

  if (mobile) {
    return (
      <div className="flex flex-col gap-2 border-t border-white/10 pt-4" data-testid="account-menu-mobile">
        <div className="flex items-center gap-3">
          <Avatar user={user} initial={initial} />
          <div className="min-w-0"><div className="font-semibold truncate">{user.name || 'Foodie'}</div><div className="text-xs text-white/50 truncate">{user.email}</div></div>
        </div>
        <Link to="/account/orders" onClick={onNavigate} className="flex items-center gap-2 text-white/85 text-sm py-1"><Package className="w-4 h-4" /> My Orders</Link>
        {user.is_admin && <Link to="/admin/dashboard" onClick={onNavigate} className="flex items-center gap-2 text-white/85 text-sm py-1"><Shield className="w-4 h-4" /> Admin</Link>}
        <button onClick={handleLogout} className="flex items-center gap-2 text-red-400 text-sm py-1"><LogOut className="w-4 h-4" /> Logout</button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button data-testid="account-menu-trigger" onClick={() => setOpen(o => !o)} className="flex items-center gap-2 group">
        <Avatar user={user} initial={initial} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-neutral-950 border border-white/10 rounded-2xl shadow-2xl p-2 z-50" data-testid="account-dropdown">
          <div className="px-3 py-2 border-b border-white/10 mb-1">
            <div className="font-semibold truncate">{user.name || 'Foodie'}</div>
            <div className="text-xs text-white/50 truncate">{user.email}</div>
          </div>
          <Link to="/account/orders" onClick={() => setOpen(false)} data-testid="my-orders-link" className="flex items-center gap-2 px-3 py-2 rounded-xl text-white/85 hover:bg-white/5 hover:text-orange-400 text-sm"><Package className="w-4 h-4" /> My Orders</Link>
          {user.is_admin && <Link to="/admin/dashboard" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-xl text-white/85 hover:bg-white/5 hover:text-orange-400 text-sm"><Shield className="w-4 h-4" /> Admin Panel</Link>}
          <button onClick={handleLogout} data-testid="customer-logout-btn" className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-red-400 hover:bg-red-500/10 text-sm"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      )}
    </div>
  );
}

function Avatar({ user, initial }) {
  return user.picture ? (
    <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover ring-2 ring-orange-500/50" />
  ) : (
    <span className="w-9 h-9 rounded-full bg-orange-500 text-white font-bold flex items-center justify-center ring-2 ring-orange-500/40"><UserIcon className="w-4 h-4" />{/* fallback */}<span className="sr-only">{initial}</span></span>
  );
}
